import { Control, FieldErrors, UseFormGetValues, UseFormRegister, UseFormResetField, UseFormSetValue, UseFormTrigger, UseFormWatch } from "react-hook-form"
import { UserDataType } from "types/autentication"
import { IBatchActionPlanRequest, INotificationPlan, NotificationType } from "types/notification"
import NotificationService from "services/Notification"
import { hasNonEmptyValue } from 'utils/variblesHandler'
import { toBoolean } from 'helper/string'

export interface GroupNotificationItem {
  number: { value: string; display_value: string }
  incident_title: { value: string; display_value: string }
  incident_description: { value: string; display_value: string }
  sys_created_on: { value: string; display_value: string }
  sys_id?: { value: string; display_value: string }
  u_image_risk?: { value: string; display_value: string }
  damage_level?: { value: string; display_value: string }
  u_probability_occurence?: { value: string; display_value: string }
  u_never_events_or_sentinel_event?: { value: string; display_value: string }
  selected?: boolean
}

export interface GroupDataInfo {
  u_notifica_user: { value: string; display_value: string }
  u_number: { value: string; display_value: string }
  u_related_notifications: { value: string; display_value: string }
  u_sector: { value: string; display_value: string }
  u_incident_type: { value: string; display_value: string }
  u_group_damage_rating: { value: string; display_value: string }
  u_location: { value: string; display_value: string }
  sys_id: { value: string; display_value: string }
}

export interface GroupData {
  group_key: string
  group_info: GroupDataInfo
  total_notifications: number
  notifications: GroupNotificationItem[]
}


export type FocusedInputType = {
  inputname?: string
  limit?: number
}
export type ToDeleteElementType = {
  index: number
  actionId: string
}
export type StepFourType = {
  sys_id: string
  id: string
  how: string
  how_much: string
  what: string
  when: any
  where: string
  who: string
  why: string
}
export const actionPlanTableHeader = [
  {
    title: 'O QUE',
    subtitle: '(SERÁ FEITO) WHAT'
  },
  {
    title: 'POR QUE',
    subtitle: '(SERÁ FEITO) WHY'
  },
  {
    title: 'ONDE',
    subtitle: '(SERÁ FEITO) WHERE'
  },
  {
    title: 'QUANDO',
    subtitle: '(SERÁ FEITO) WHEN'
  },
  {
    title: 'POR QUEM',
    subtitle: '(SERÁ FEITO) WHO'
  },
  {
    title: 'COMO',
    subtitle: '(SERÁ FEITO) HOW'
  },
  {
    title: 'QUANTO',
    subtitle: '(CUSTARÁ) HOW MUCH'
  }
]
export type ErrorStep4Type = FieldErrors<{
  fields: {
    sys_id?: string
    id: string
    how: string
    how_much: string
    what: string
    when: string
    where: string
    who: string
    why: string
  }[]
}>
export interface GroupTreatmentsStep4Props {
  id: string
  notificationById: NotificationType
  notificationStatusState: string
  tooltipAlert: string
  statusIsArchivedOrFinished: boolean
  currentUser: UserDataType
  groupData?: GroupData
  onActionPlanChange?: (isCompleted: boolean) => void
  loggedInUserIsResponsible?: boolean
}
export interface IGroupActionsComponent {
  id: string
  trigger: UseFormTrigger<any>
  actionPlans: INotificationPlan[]
  notificationById: NotificationType
  notificationStatusState: string
  tooltipAlert: string
  register: UseFormRegister<{ fields: StepFourType[] }>
  setValue: UseFormSetValue<{ fields: StepFourType[] }>
  resetField: UseFormResetField<{ fields: StepFourType[] }>
  control: Control<{ fields: StepFourType[] }, any>
  isDirty: boolean
  statusIsArchivedOrFinished: boolean
  reset: any
  getValues: UseFormGetValues<{ fields: StepFourType[] }>
  watch: UseFormWatch<{ fields: StepFourType[] }>
  errors: ErrorStep4Type
  currentUser: UserDataType
  groupData?: GroupData
  dirtyFieldsStep4: Partial<
    Readonly<{
      fields?: {
        sys_id?: boolean
        id?: boolean
        how?: boolean
        how_much?: boolean
        what?: boolean
        when?: boolean
        where?: boolean
        who?: boolean
        why?: boolean
      }[]
    }>
  >
}
export interface GroupActionPlanTableBodyProps {
  trigger: UseFormTrigger<any>
  disabledForRole: boolean
  index: number
  isStaticData?:boolean
  register?: UseFormRegister<{ fields: StepFourType[] }>
  control?: Control<{ fields: StepFourType[] }, any>
  handleDelete: (idx: number) => void
  setFocusedInputData?: React.Dispatch<
    React.SetStateAction<{
      inputname?: string
      limit?: number
    }>
  >
  errors: ErrorStep4Type
  setValue?: UseFormSetValue<{ fields: StepFourType[] }>
  getValues?: UseFormGetValues<{ fields: StepFourType[] }>
}
export const listOfQuestions = [
  'how',
  'what',
  'when',
  'where',
  'who',
  'why',
  'how_much'
]
export type QuestionsTypes = 'how' | 'what' | 'when' | 'where' | 'who' | 'why' | 'how_much';

/**
 * Formulário “vazio”: sem linhas, ou só linhas novas sem dados (ex.: pós-lixeira).
 * Mesma noção de hasNonEmptyValue usada no delete de linha.
 */
export function isGroupActionPlanFormEffectivelyEmpty(
  fields: StepFourType[] | undefined
): boolean {
  if (!fields?.length) return true
  return fields.every(
    (row) => !row.sys_id?.trim() && !hasNonEmptyValue(row)
  )
}

/** sys_id da notificação no grupo — alinhado a getGroupNotificationSysId (Londres/Ishikawa) */
export function groupNotificationSysId(n?: GroupNotificationItem | null): string {
  if (!n?.sys_id) return ''
  const s = n.sys_id as string | { value?: string }
  if (typeof s === 'string' && s.trim() !== '') return s.trim()
  if (typeof s === 'object' && typeof s.value === 'string' && s.value.trim() !== '') return s.value.trim()
  return ''
}

/**
 * Ishikawa: um GET por notificação em paralelo. Aqui: lista de planos por notificação.
 */
export async function buildActionPlanListsByNotificationMap(
  groupData: GroupData
): Promise<Map<string, INotificationPlan[]>> {
  const map = new Map<string, INotificationPlan[]>()
  const notifications =
    groupData?.notifications?.filter((n) => n.selected !== false) || []

  await Promise.all(
    notifications.map(async (n) => {
      const notifId = groupNotificationSysId(n)
      if (!notifId) return
      try {
        const plans = await NotificationService.fetchActionPlansByNotificationId(notifId)
        if (plans.length > 0) map.set(notifId, plans)
      } catch {
        /* merge Redux pode preencher a primeira notificação */
      }
    })
  )

  return map
}

/**
 * Garante ordem/sys_id da 1ª notificação iguais ao Redux (formulário), como mergeFirstIshikawaIdFromRedux.
 */
export function mergeFirstNotificationActionPlansFromRedux(
  groupData: GroupData,
  map: Map<string, INotificationPlan[]>,
  actionPlansFromRedux?: INotificationPlan[] | null
): Map<string, INotificationPlan[]> {
  const out = new Map(map)
  const first = groupData.notifications?.[0]
  const firstId = groupNotificationSysId(first)
  if (firstId && actionPlansFromRedux?.length) {
    out.set(firstId, actionPlansFromRedux)
  }
  return out
}

/**
 * Calcula se o plano de ação é obrigatório para um grupo baseado nas notificações do grupo
 * @param groupData - Dados do grupo incluindo todas as notificações
 * @returns boolean - true se o plano de ação deve ser obrigatório
 */
export function calculateGroupActionPlanMandatory(groupData?: GroupData): boolean {
  if (!groupData?.notifications) {
    return false;
  }

  // Se qualquer notificação do grupo tornar o plano de ação obrigatório, 
  // então o plano do grupo deve ser obrigatório
  return groupData.notifications.some(notification => {
    // Verificar se tem risco de imagem
    const hasImageRisk = notification.u_image_risk?.value === 'u_image_risk_yes' || 
                        notification.u_image_risk?.value === 'true';
    
    // Verificar se tem Never Events
    const hasNeverEvents = notification.u_never_events_or_sentinel_event?.value === 'yes';
    
    // Se tem risco de imagem ou Never Events, é obrigatório
    if (hasImageRisk || hasNeverEvents) {
      return true;
    }

    // Verificar grau de dano crítico (Óbito ou Grave)
    const damageLevel = notification.damage_level?.value;
    const criticalDamages = ['death', 'severe']; // valores que precisam ser verificados na base
    
    if (criticalDamages.includes(damageLevel || '')) {
      return true;
    }

    // Para outros casos, verificar a probabilidade de ocorrência
    const probability = parseInt(notification.u_probability_occurence?.value || '0');
    
    // Se probabilidade alta (4 ou 5), pode ser obrigatório dependendo do dano
    if (probability >= 4) {
      return true;
    }

    return false;
  });
}

function planRecordSysId(plan: INotificationPlan | Record<string, unknown>): string {
  const s = (plan as Record<string, unknown>).sys_id
  if (typeof s === 'string') return s
  if (s && typeof s === 'object' && s !== null && 'value' in s) {
    return String((s as { value?: string }).value || '')
  }
  return ''
}

function planTextField(
  plan: INotificationPlan | Record<string, unknown>,
  key: string
): string {
  const v = (plan as Record<string, unknown>)[key]
  if (typeof v === 'string') return v.trim()
  if (v && typeof v === 'object' && v !== null && 'value' in v) {
    return String((v as { value?: string }).value || '').trim()
  }
  return ''
}

function planWhenRaw(plan: INotificationPlan | Record<string, unknown>): string {
  return planTextField(plan, 'when')
}

/** Parse de data para ordenar planos iguais entre notificações (DD/MM/AAAA ou ISO). */
function actionPlanWhenSortKey(whenStr: string): number {
  const s = whenStr.trim()
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s)
  if (m) {
    const [, d, mo, y] = m
    const t = new Date(`${y}-${mo}-${d}T12:00:00`).getTime()
    return Number.isFinite(t) ? t : 0
  }
  const t = Date.parse(s)
  return Number.isFinite(t) ? t : 0
}

/**
 * Chave semântica alinhada entre notificações (sem sys_id): mesma linha de grupo compartilha a mesma ordem.
 * A ordem crua da API pode ser 1,2,3 em uma notificação e 1,3,2 em outra — o índice nuño falha.
 */
function actionPlanAlignmentSortKey(plan: INotificationPlan | Record<string, unknown>): string {
  const t = actionPlanWhenSortKey(planWhenRaw(plan))
  const fields = ['what', 'who', 'where', 'how', 'why', 'how_much'].map((k) =>
    planTextField(plan, k)
  )
  return [String(t).padStart(15, '0'), ...fields].join('\u0001')
}

export function sortPlansForGroupAlignment(plans: INotificationPlan[]): INotificationPlan[] {
  return [...plans].sort((a, b) =>
    actionPlanAlignmentSortKey(a).localeCompare(actionPlanAlignmentSortKey(b), 'en', {
      sensitivity: 'base'
    })
  )
}

/** Todos os sys_id de planos no grupo (para delete em lote ao salvar lista vazia). */
export function collectAllActionPlanSysIdsFromMap(
  plansByNotificationId: Map<string, INotificationPlan[]>
): string[] {
  const set = new Set<string>()
  for (const list of plansByNotificationId.values()) {
    for (const p of list) {
      const sid = planRecordSysId(p)
      if (sid) set.add(sid)
    }
  }
  return [...set]
}

function isNotPerformOptionalTrue(plan: INotificationPlan | Record<string, unknown>): boolean {
  const v = (plan as Record<string, unknown>).u_do_not_perform_optional
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') return toBoolean(v)
  return false
}

/**
 * Plano criado pelo fluxo "Não realizar o preenchimento" (CREATE com linha vazia).
 * Ao desmarcar, esses registros devem ser excluídos em batch — só atualizar a flag deixa linha em branco na UI.
 */
export function isNotPerformPlaceholderPlan(
  plan: INotificationPlan | Record<string, unknown>
): boolean {
  if (!isNotPerformOptionalTrue(plan)) return false
  const textKeys = ['what', 'who', 'where', 'how', 'why', 'when'] as const
  for (const k of textKeys) {
    if (planTextField(plan, k)) return false
  }
  const hm = planTextField(plan, 'how_much')
  if (hm && hm !== 'R$ 0,00') return false
  return true
}

/** sys_id de planos que são só placeholder de "não realizar", para delete ao desmarcar o checkbox. */
export function collectPlaceholderNotPerformSysIdsFromMap(
  plansByNotificationId: Map<string, INotificationPlan[]>
): string[] {
  const set = new Set<string>()
  for (const list of plansByNotificationId.values()) {
    for (const p of list) {
      if (isNotPerformPlaceholderPlan(p)) {
        const sid = planRecordSysId(p)
        if (sid) set.add(sid)
      }
    }
  }
  return [...set]
}

function hospitalIncidentId(plan: INotificationPlan): string {
  const p = plan as unknown as Record<string, unknown>
  const hi = p.hospital_incident
  if (hi && typeof hi === 'object' && hi !== null) {
    const o = hi as Record<string, unknown>
    if (o.value !== undefined && o.value !== null) {
      return String(o.value)
    }
    const link = o.link
    if (typeof link === 'string' && link.length > 0) {
      const parts = link.split('/').filter(Boolean)
      const id32 = parts.find((s) => /^[a-f0-9]{32}$/i.test(s))
      if (id32) return id32
    }
  }
  if (typeof hi === 'string') return hi
  if (typeof p.hospital_incident_id === 'string') return p.hospital_incident_id
  return ''
}

/**
 * O endpoint do grupo pode devolver array solto, `result`, ou lista aninhada por notificação.
 */
export function normalizeGroupActionPlansList(raw: unknown): INotificationPlan[] {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw.filter(Boolean) as INotificationPlan[]
  }
  if (typeof raw !== 'object') return []

  const o = raw as Record<string, unknown>

  if (Array.isArray(o.result)) {
    return normalizeGroupActionPlansList(o.result)
  }
  if (Array.isArray(o.action_plans)) {
    return o.action_plans as INotificationPlan[]
  }
  if (Array.isArray(o.plans)) {
    return o.plans as INotificationPlan[]
  }
  if (Array.isArray(o.items)) {
    return o.items as INotificationPlan[]
  }
  if (Array.isArray(o.notifications)) {
    const acc: INotificationPlan[] = []
    for (const block of o.notifications as Record<string, unknown>[]) {
      if (!block || typeof block !== 'object') continue
      const inner =
        block.action_plans ||
        block.plans ||
        block.u_action_plans ||
        []
      if (Array.isArray(inner)) {
        acc.push(...(inner as INotificationPlan[]))
      }
    }
    return acc
  }

  if ('sys_id' in o && (typeof o.sys_id === 'string' || (o.sys_id && typeof o.sys_id === 'object'))) {
    return [o as unknown as INotificationPlan]
  }

  return []
}

/**
 * Agrupa planos por notificação (hospital_incident). Preserva a ordem da API em cada lista.
 */
export function buildActionPlansByNotificationId(
  allGroupPlans: INotificationPlan[]
): Map<string, INotificationPlan[]> {
  const map = new Map<string, INotificationPlan[]>()
  for (const plan of allGroupPlans) {
    const hid = hospitalIncidentId(plan)
    if (!hid) continue
    const list = map.get(hid) || []
    list.push(plan)
    map.set(hid, list)
  }
  return map
}

/**
 * UPDATE em lote: mesmo desenho do Ishikawa — um sys_id por notificação e slot,
 * usando listas obtidas com GET por notificação (Map), não o endpoint agregado do grupo.
 */
export function convertGroupActionPlanFieldsToUpdateBatchRequest(
  fields: StepFourType[],
  groupData: GroupData,
  plansByNotificationId: Map<string, INotificationPlan[]>,
  currentUser: UserDataType
): IBatchActionPlanRequest {
  if (!groupData?.notifications?.length) {
    throw new Error('Dados do grupo não encontrados')
  }

  const notifications = groupData.notifications.filter((n) => n.selected !== false)
  const uUser =
    currentUser?.sys_id || groupData.group_info.u_notifica_user?.value || ''

  const records: IBatchActionPlanRequest['records'] = []

  const firstNotifId = groupNotificationSysId(notifications[0])
  const firstListRaw = firstNotifId ? plansByNotificationId.get(firstNotifId) || [] : []
  const firstList = sortPlansForGroupAlignment(firstListRaw)

  for (let i = 0; i < fields.length; i++) {
    const row = fields[i]
    if (!row.sys_id?.trim()) continue

    let slotIndex = firstList.findIndex((p) => planRecordSysId(p) === row.sys_id.trim())
    if (slotIndex < 0) slotIndex = i

    for (const n of notifications) {
      const notifId = groupNotificationSysId(n)
      if (!notifId) continue
      const listForNotif = sortPlansForGroupAlignment(
        plansByNotificationId.get(notifId) || []
      )
      const target = listForNotif[slotIndex]
      const targetId = target ? planRecordSysId(target) : ''
      if (!targetId) continue

      records.push({
        sys_id: targetId,
        hospital_incident: notifId,
        how: row.how || '',
        how_much: row.how_much || '',
        what: row.what || '',
        when: row.when || '',
        where: row.where || '',
        who: row.who || '',
        why: row.why || '',
        u_notifica_user: uUser,
        u_do_not_perform_optional: 'false'
      } as IBatchActionPlanRequest['records'][number])
    }
  }

  if (records.length === 0) {
    for (const row of fields) {
      if (!row.sys_id?.trim()) continue
      records.push({
        sys_id: row.sys_id,
        hospital_incident: firstNotifId || '',
        how: row.how || '',
        how_much: row.how_much || '',
        what: row.what || '',
        when: row.when || '',
        where: row.where || '',
        who: row.who || '',
        why: row.why || '',
        u_notifica_user: uUser,
        u_do_not_perform_optional: 'false'
      } as IBatchActionPlanRequest['records'][number])
    }
  }

  return {
    records,
    options: { stopOnError: false }
  }
}

/**
 * UPDATE "não realizar" em lote para todas as notificações (um sys_id por notificação e slot).
 */
export function convertNotPerformToUpdateBatchRequestForGroup(
  templatePlansOrdered: INotificationPlan[],
  plansByNotificationId: Map<string, INotificationPlan[]>,
  groupData: GroupData,
  currentUser: UserDataType,
  doNotPerform: boolean
): IBatchActionPlanRequest {
  if (!templatePlansOrdered.length) {
    return { records: [], options: { stopOnError: false } }
  }

  const notifications = groupData.notifications.filter((n) => n.selected !== false)
  const uUser =
    currentUser?.sys_id || groupData.group_info.u_notifica_user?.value || ''

  const records: IBatchActionPlanRequest['records'] = []

  const templateSorted = sortPlansForGroupAlignment([...templatePlansOrdered])

  templateSorted.forEach((templateRow, slotIndex) => {
    for (const n of notifications) {
      const notifId = groupNotificationSysId(n)
      if (!notifId) continue
      const listForNotif = sortPlansForGroupAlignment(
        plansByNotificationId.get(notifId) || []
      )
      const target = listForNotif[slotIndex]
      const targetId = target ? planRecordSysId(target) : ''
      if (!targetId) continue

      records.push({
        sys_id: targetId,
        hospital_incident: notifId,
        u_do_not_perform_optional: doNotPerform ? 'true' : 'false',
        how: templateRow.how || ' ',
        how_much: templateRow.how_much || ' ',
        what: templateRow.what || ' ',
        when: templateRow.when || ' ',
        where: templateRow.where || ' ',
        who: templateRow.who || ' ',
        why: templateRow.why || ' ',
        u_notifica_user: uUser
      } as IBatchActionPlanRequest['records'][number])
    }
  })

  if (records.length === 0) {
    templateSorted.forEach((templateRow) => {
      const sid = planRecordSysId(templateRow)
      if (!sid) return
      records.push({
        sys_id: sid,
        hospital_incident: hospitalIncidentId(templateRow),
        u_do_not_perform_optional: doNotPerform ? 'true' : 'false',
        how: templateRow.how || ' ',
        how_much: templateRow.how_much || ' ',
        what: templateRow.what || ' ',
        when: templateRow.when || ' ',
        where: templateRow.where || ' ',
        who: templateRow.who || ' ',
        why: templateRow.why || ' ',
        u_notifica_user: uUser
      } as IBatchActionPlanRequest['records'][number])
    })
  }

  return {
    records,
    options: { stopOnError: false }
  }
}

/**
 * Converte dados do plano de ação de grupo para formato batch
 * @param actionPlan - Plano de ação individual
 * @param groupData - Dados do grupo com notificações
 * @param currentUser - Usuário atual
 * @returns IBatchActionPlanRequest - Request formatado para batch
 */
export function convertGroupActionPlanToBatchRequest(
  actionPlan: StepFourType, 
  groupData: GroupData, 
  currentUser: UserDataType
): any {
  if (!groupData?.notifications) {
    throw new Error('Dados do grupo não encontrados');
  }

  // Preparar records para todas as notificações selecionadas do grupo
  const records = groupData.notifications
    .filter(notification => notification.selected !== false) // Include only selected notifications
    .map(notification => ({
      hospital_incident: notification.sys_id?.value || '',
      how: actionPlan.how || '',
      how_much: actionPlan.how_much || '',
      what: actionPlan.what || '',
      when: actionPlan.when || '',
      where: actionPlan.where || '',
      who: actionPlan.who || '',
      why: actionPlan.why || '',
      u_notifica_user: currentUser?.sys_id || groupData.group_info.u_notifica_user?.value || '',
      u_do_not_perform_optional: 'false' // Por padrão, assumir que deve ser realizado
    }));

  return {
    records,
    options: {
      stopOnError: false // Continuar processamento mesmo se houver erro em uma notificação
    }
  };
}

/**
 * Converte dados do formulário "Não realizar" para formato batch CREATE
 * @param groupData - Dados do grupo com notificações
 * @param currentUser - Usuário atual
 * @param doNotPerform - Se deve marcar como "não realizar"
 * @returns IBatchActionPlanRequest - Request formatado para batch CREATE
 */
export function convertNotPerformToBatchRequest(
  groupData: GroupData,
  currentUser: UserDataType,
  doNotPerform: boolean = true
): any {
  if (!groupData?.notifications) {
    throw new Error('Dados do grupo não encontrados');
  }

  // Preparar records para todas as notificações do grupo (CREATE)
  const records = groupData.notifications.map(notification => ({
    hospital_incident: notification.sys_id?.value || '',
    how: " ",
    how_much: " ",
    what: " ",
    when: " ",
    where: " ",
    who: " ",
    why: " ",
    u_notifica_user: currentUser?.sys_id || groupData.group_info.u_notifica_user?.value || '',
    u_do_not_perform_optional: doNotPerform
  }));

  return {
    records,
    options: {
      stopOnError: false // Continuar processamento mesmo se houver erro
    }
  };
}

/**
 * Converte dados do formulário "Não realizar" para formato batch UPDATE
 * @param existingPlans - Planos existentes que serão atualizados
 * @param currentUser - Usuário atual
 * @param doNotPerform - Se deve marcar como "não realizar"
 * @param groupData - Dados do grupo (para fallback de usuário)
 * @returns IBatchActionPlanRequest - Request formatado para batch UPDATE
 */
export function convertNotPerformToUpdateBatchRequest(
  existingPlans: any[],
  currentUser: UserDataType,
  doNotPerform: boolean = true,
  groupData?: GroupData
): any {
  if (!existingPlans || existingPlans.length === 0) {
    throw new Error('Nenhum plano existente encontrado para atualizar');
  }

  // Para UPDATE, manter todos os campos existentes e só alterar u_do_not_perform_optional
  const records = existingPlans.map(actionPlan => ({
    sys_id: actionPlan.sys_id, // Obrigatório para UPDATE
    u_do_not_perform_optional: doNotPerform,
    // Manter todos os outros campos existentes
    how: actionPlan.how || " ",
    how_much: actionPlan.how_much || " ",
    what: actionPlan.what || " ",
    when: actionPlan.when || " ",
    where: actionPlan.where || " ",
    who: actionPlan.who || " ",
    why: actionPlan.why || " ",
    u_notifica_user: currentUser?.sys_id || actionPlan.u_notifica_user || groupData?.group_info?.u_notifica_user?.value || ''
  }));

  return {
    records,
    options: {
      stopOnError: false // Continuar processamento mesmo se houver erro
    }
  };
}

/**
 * DELETE em lote: mesmo desenho do UPDATE — um sys_id por notificação e slot,
 * usando o Map por notificação (GET por notificação), não só o sys_id do formulário (1ª notificação).
 */
function expandDeleteSysIdsForGroup(
  actionPlansToDelete: StepFourType[],
  groupData: GroupData,
  plansByNotificationId: Map<string, INotificationPlan[]>
): string[] {
  if (!groupData.notifications?.length) return []
  const notifications = groupData.notifications.filter((n) => n.selected !== false)
  if (!notifications.length) return []

  const firstNotifId = groupNotificationSysId(notifications[0])
  const firstListRaw = firstNotifId ? plansByNotificationId.get(firstNotifId) || [] : []
  const firstList = sortPlansForGroupAlignment(firstListRaw)

  const records: string[] = []

  for (let i = 0; i < actionPlansToDelete.length; i++) {
    const row = actionPlansToDelete[i]
    if (!row.sys_id?.trim()) continue

    const sid = row.sys_id.trim()
    let slotIndex = firstList.findIndex((p) => planRecordSysId(p) === sid)
    if (slotIndex < 0) slotIndex = i

    for (const n of notifications) {
      const notifId = groupNotificationSysId(n)
      if (!notifId) continue
      const listForNotif = sortPlansForGroupAlignment(
        plansByNotificationId.get(notifId) || []
      )
      const target = listForNotif[slotIndex]
      const targetId = target ? planRecordSysId(target) : ''
      if (targetId) records.push(targetId)
    }
  }

  if (records.length === 0) {
    for (const row of actionPlansToDelete) {
      if (!row.sys_id?.trim()) continue
      records.push(row.sys_id.trim())
    }
  }

  return [...new Set(records)]
}

/**
 * Converte planos de ação para delete em formato batch
 * @param actionPlansToDelete - Array com os planos de ação a serem deletados
 * @param groupData - Dados do grupo com notificações  
 * @param currentUser - Usuário atual (mantido na assinatura por compatibilidade)
 * @param plansByNotificationId - Opcional: mapa GET por notificação; quando preenchido, expande o delete para todas as notificações do grupo (por slot)
 */
export function convertDeleteActionPlanToBatchRequest(
  actionPlansToDelete: StepFourType[],
  groupData: GroupData,
  currentUser: UserDataType,
  plansByNotificationId?: Map<string, INotificationPlan[]>
): any {
  if (!groupData?.notifications) {
    throw new Error('Dados do grupo não encontrados');
  }

  if (!actionPlansToDelete || actionPlansToDelete.length === 0) {
    throw new Error('Nenhum plano de ação para deletar');
  }

  void currentUser

  const records =
    plansByNotificationId && plansByNotificationId.size > 0
      ? expandDeleteSysIdsForGroup(actionPlansToDelete, groupData, plansByNotificationId)
      : actionPlansToDelete
          .filter((plan) => plan.sys_id && plan.sys_id.trim() !== '')
          .map((p) => p.sys_id.trim())

  return {
    records,
    options: {
      stopOnError: false
    }
  };
}

/**
 * Função auxiliar para deletar múltiplos planos de ação de uma vez
 * Útil para operações em massa como "deletar todos os planos selecionados"
 * @param selectedPlans - Array dos planos selecionados para delete
 * @param groupData - Dados do grupo
 * @param currentUser - Usuário atual
 * @param dispatch - Dispatch do Redux
 * @param groupId - ID do grupo para recarregar dados após delete
 */
export async function deleteMultipleActionPlans(
  selectedPlans: StepFourType[],
  groupData: GroupData,
  currentUser: UserDataType,
  dispatch: any,
  groupId: string
): Promise<void> {
  if (selectedPlans.length === 0) {
    console.warn('Nenhum plano selecionado para deletar');
    return;
  }

  try {
    const plansByNotif = await buildActionPlanListsByNotificationMap(groupData)
    const mergedPlansByNotif = mergeFirstNotificationActionPlansFromRedux(
      groupData,
      plansByNotif,
      null
    )
    const deleteBatchRequest = convertDeleteActionPlanToBatchRequest(
      selectedPlans,
      groupData,
      currentUser,
      mergedPlansByNotif
    );

    // Importar as actions dinamicamente para evitar dependência circular
    const NotificationActions = await import('actions/Notification');
    
    await NotificationActions.default.deleteActionPlanBatch(
      dispatch,
      deleteBatchRequest
    );

    // Recarregar dados
    NotificationActions.default.getListOfActionPlanGroup(dispatch, groupId);
  } catch (error) {
    console.error('Erro ao deletar múltiplos planos:', error);
    throw error;
  }
}